import { Feature, Map, View } from 'ol'
import { extend, Extent } from 'ol/extent'
import { Geometry } from 'ol/geom'
import { fromLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { MapContext } from './MapContext'
import { useMapSettings } from './MapSettings/useMapSettings'
import { FitExtentOpts } from './types'

type TMapProviderProps = { refMap: HTMLDivElement; children: ReactNode }

export const MapProvider = (props: TMapProviderProps): ReactElement => {
  const { refMap, children } = props

  const { settings } = useMapSettings()

  const map = useMemo(() => {
    return new Map({
      layers: [],
      view: new View({
        center: fromLonLat(settings.center),
        maxZoom: 22,
        zoom: settings.zoom,
        minZoom: 4,
      }),
      controls: [],
      pixelRatio: window.devicePixelRatio,
    })
  }, [])

  useEffect(() => {
    map.setTarget(refMap)
  }, [map])

  const jumpTo = useCallback(
    (lonlat: [number, number], zoom?: number): void => {
      map.getView().animate({
        center: fromLonLat(lonlat),
        zoom: zoom ?? 9,
        duration: 300,
      })
    },
    [map],
  )

  const zoom = (zoom: number): void => {
    map.getView().animate({
      zoom,
      duration: 300,
    })
  }

  const fitExtent = (extent?: Extent | null, opts?: FitExtentOpts): void => {
    if (!extent) return
    const {
      maxZoom = 16,
      duration = 300,
      padding = [120, 50, 50, 50],
    } = opts ?? {}

    map.getView().fit(extent, {
      size: map.getSize(),
      duration,
      padding,
      maxZoom,
    })
  }

  const centerAllElements = (opts?: FitExtentOpts): void => {
    let ext: Extent | null = null
    map.getAllLayers().forEach((layer) => {
      const src = layer.getSource()
      if (src instanceof VectorSource) {
        const features = src.getFeatures() as Feature<Geometry>[]
        features.forEach((feature) => {
          const geom = feature.getGeometry()
          if (geom) {
            const extent = geom.getExtent()
            if (ext) {
              extend(ext, extent)
            } else {
              ext = extent
            }
          }
        })
      }
    })
    fitExtent(ext, opts)
  }

  // Required to prevent constant re-render
  const ctxValue = useMemo(
    () => ({
      map,
      refMap,
      jumpTo,
      zoom,
      centerAllElements,
      fitExtent,
    }),
    [map, refMap, jumpTo, zoom, centerAllElements, fitExtent],
  )

  return (
    <>
      <MapContext.Provider value={ctxValue}>{children}</MapContext.Provider>
    </>
  )
}
